import { Box, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min.js";
import { selectEmployeeBidData, updateSortModel } from "../../slices/employeeBid/employeeBidSlice";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { BID_STATUS } from "../../util/bid/bidConstants.js";
import { getSortedBidsByStartDateAndEndDate, getSortedBidsByStatus } from "../../util/bid/sortBids.js";
import { ACTIONS, BID_PAGE_TYPE, FEATURES } from "../../util/constants.js";
import { getShiftDate } from "../../util/dateTimeFormatter.js";
import { getLastUpdatedCustomComparator } from "../../util/sortTrades.js";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay.jsx";
import CustomLoadingTableOverlay from "../CustomLoadingTableOverlay.jsx";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay.jsx";
import ActionButton from "../fundamentals/ActionButton";

const useStyles = makeStyles((theme) => ({
  myBidsTable: (props) => ({
    "& div": {
      "& .MuiDataGrid-footerContainer": {
        display: "none",
      },
    },
    borderRadius: "0px",
  }),
  "& .MuiDataGrid-columnHeaderTitle": {
    textOverflow: "clip",
    whiteSpace: "break-spaces",
    lineHeight: 1.2,
    textAlign: "left",
  },
}));

const MyBidsTable = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const userSession = useSelector(selectUserSession);
  const [selectionModel, setSelectionModel] = React.useState([]);
  const employeeBidData = useSelector(selectEmployeeBidData);
  const dispatch = useDispatch();

  const editBid = (bid) => {
    const bidChildItems = props.featureConfig.childItems;
    const bidChoicesFeature = bidChildItems.find((item) => item.key === FEATURES.BID_CHOICES);
    history.push({
      pathname: bidChoicesFeature.path
        .replace(":referenceNumber", bid.referenceNumber)
        .replace(":eid", userSession.eid),
    });
  };

  const renderBidType = (bid) => {
    return bid.bidType;
  };

  const renderStatus = (bid) => {
    return bid.status;
  };

  const renderWindowDate = (bid) => {
    return bid.windowStartDate
      ? `${getShiftDate(bid.windowStartDate)} - ${getShiftDate(bid.windowEndDate)}, ${bid.windowStartDate.toFormat(
          "HH:mm ZZZZ"
        )}`
      : "";
  };

  const renderSchedDate = (bid) => {
    return bid.schedStartDate ? `${getShiftDate(bid.schedStartDate)} - ${getShiftDate(bid.schedEndDate)}` : "";
  };

  const renderActionsCell = (value) => {
    return value.status === BID_STATUS.AWARDED ? (
      <Grid>
        <ActionButton
          variant="contained"
          color="primary"
          onClick={() => {
            props.setSelectedAwardedBidChoiceData(value);
          }}
        >
          RESULTS
        </ActionButton>
      </Grid>
    ) : value.status === BID_STATUS.OPEN || value.status === BID_STATUS.CLOSED ? (
      <Grid>
        <ActionButton variant="contained" color="primary" onClick={() => editBid(value)}>
          BID FORM
        </ActionButton>
      </Grid>
    ) : (
      ""
    );
  };

  const renderBidTable = () => {
    const columns = [
      {
        field: "createdAt",
        headerName: "Last Updated",
        type: "dateTime",
        valueFormatter: (params) => {
          return params.value.toFormat("LL/dd/yyyy T ZZZZ");
        },
        sortComparator: getLastUpdatedCustomComparator,
        flex: 0.2,
      },
      {
        field: "station",
        headerName: "Station",
        valueFormatter: (params) => {
          return params.value;
        },
        flex: 0.1,
      },
      {
        field: "bidType",
        renderCell: (params) => {
          return renderBidType(params.row);
        },
        headerName: "Bid Type",
        type: "string",
        flex: 0.14,
      },
      { field: "workgroup", headerName: "Workgroup", type: "string", flex: 0.14 },
      {
        field: "schedDates",
        valueGetter: (params) => {
          return {
            startDate: params.row.schedStartDate,
            endDate: params.row.schedEndDate,
          };
        },
        renderCell: (params) => {
          return renderSchedDate(params.row);
        },
        headerName: "Bid Effective Dates",
        type: "string",
        flex: 0.25,
        minWidth: 170,
        sortComparator: getSortedBidsByStartDateAndEndDate,
      },
      {
        field: "windowDates",
        valueGetter: (params) => {
          return {
            startDate: params.row.windowStartDate,
            endDate: params.row.windowEndDate,
          };
        },
        renderCell: (params) => {
          return renderWindowDate(params.row);
        },
        headerName: "Bid Window",
        type: "string",
        flex: 0.25,
        minWidth: 245,
        sortComparator: getSortedBidsByStartDateAndEndDate,
      },

      {
        field: "status",
        valueGetter: (params) => {
          return params.value;
        },
        valueFormatter: ({ value }) => {
          return value;
        },
        renderCell: (params) => {
          return renderStatus(params.row);
        },
        headerName: "Status",
        type: "string",
        flex: 0.2,
        sortComparator: getSortedBidsByStatus,
      },
      {
        field: "action",
        headerName: ACTIONS,
        type: "string",
        flex: 0.15,
        sortable: false,
        renderCell: (params) => {
          return renderActionsCell(params.row);
        },
      },
    ];

    return (
      <div style={{ width: "100%", height: "inherit", paddingLeft: "2rem", paddingRight: "2rem" }}>
        <DataGridPro
          className={classes.myBidsTable}
          apiRef={props.tableDesktopRef}
          rows={props.bidList}
          columns={columns}
          sortModel={employeeBidData.sortModel}
          onSortModelChange={(event) => {
            dispatch(updateSortModel(event));
          }}
          sortingOrder={["asc", "desc"]}
          disableColumnMenu={true}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          selectionModel={selectionModel}
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true;
            setSelectionModel([params.id]);
          }}
          onRowClick={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          hideFooterPagination={true}
          rowHeight={50}
          components={{
            LoadingOverlay: CustomLoadingTableOverlay,
            NoRowsOverlay: CustomNoRowsTableOverlay,
            ErrorOverlay: CustomErrorTableOverlay,
            Toolbar: null,
          }}
          componentsProps={{
            noRowsOverlay: { pageType: BID_PAGE_TYPE },
            errorOverlay: {
              errorMessage: props.errorObject.message,
              errorCode: props.errorObject.code,
            },
            toolbar: { station: props.station },
          }}
          loading={props.isLoading}
          error={props.isError}
        />
      </div>
    );
  };
  return <Box sx={{ height: `calc(85vh - 210px)` }}>{renderBidTable()}</Box>;
};

MyBidsTable.propTypes = {
  bidList: PropTypes.array.isRequired,
  station: PropTypes.string,
  isFirstLoad: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  errorObject: PropTypes.object.isRequired,
  tableDesktopRef: PropTypes.object.isRequired,
  setSelectedAwardedBidChoiceData: PropTypes.func.isRequired,
};

export default MyBidsTable;
